import { z } from "zod";
import {
  moduleThemeSchema,
  moduleThemeCss,
  resolveModuleTheme,
} from "@proof/core";

export const authoringModes = [
  "designer",
  "configuration",
  "reusable-forms",
  "outputs",
  "global-profiles",
  "validation-presets",
  "formatting-presets",
  "summary",
] as const;
export type AuthoringMode = (typeof authoringModes)[number];
export const integrationOptionsSchema = z
  .object({
    designMode: z.boolean().default(false),
    editor: z
      .object({
        enableStatements: z.boolean().optional(),
        enableRules: z.boolean().optional(),
        enableFormatting: z.boolean().optional(),
        enableValidation: z.boolean().optional(),
        enableDynamicSources: z.boolean().optional(),
        enableConditionalVisibility: z.boolean().optional(),
        enablePartOverrides: z.boolean().optional(),
        allowProjectRename: z.boolean().default(false),
        showHeading: z.boolean().default(false),
        showDescription: z.boolean().default(false),
      })
      .strict()
      .default({
        allowProjectRename: false,
        showHeading: false,
        showDescription: false,
      }),
    theme: moduleThemeSchema.default({}),
    allowedModes: z.array(z.enum(authoringModes)).default([...authoringModes]),
    permissions: z
      .object({
        edit: z.boolean().default(true),
        proof: z.boolean().default(true),
        export: z.boolean().default(true),
      })
      .strict()
      .default({ edit: true, proof: true, export: true }),
  })
  .strict();
export type IntegrationOptions = z.input<typeof integrationOptionsSchema>;

/** Shared shadow-root tokens. Artwork keeps its own document colours. */
export function integrationThemeCss(input: IntegrationOptions["theme"] = {}) {
  const theme = resolveModuleTheme({ company: moduleThemeSchema.parse(input) });
  return (
    (
      moduleThemeCss(theme) + moduleThemeCss(theme, ".proof-integration")
    ).replaceAll(
      ':root:not([data-theme="dark"])',
      '.proof-integration:not([data-theme="dark"])',
    ) +
    `
.proof-integration{--primary:${theme.primary};--blue:${theme.accent};--module-radius:${theme.radius}px;font-size:${theme.fontSize}px;color:${theme.ink};background:${theme.background};--integration-surface:${theme.surface};--integration-border:${theme.border}}
.proof-integration[data-theme="dark"]{color:#e4e9e6;background:#181d1b;--ink:#e4e9e6;--muted:#a8b4ae;--line:#46534c;--integration-surface:#242c27;--integration-border:#46534c;color-scheme:dark}
.proof-integration>.embedded-form{padding:12px}
.proof-integration .embedded-dialog,.proof-integration input:not([type=checkbox]):not([type=radio]):not([type=color]):not(:disabled),.proof-integration select:not(:disabled),.proof-integration textarea:not(:disabled){color:inherit;background:var(--integration-surface);border-color:var(--integration-border);border-radius:${theme.radius}px}
.proof-integration>button,.proof-integration .embedded-dialog-header>button{background:var(--primary);color:white;border:1px solid var(--primary);border-radius:${theme.radius}px}
.proof-integration fieldset.integration-controls{margin:0;padding:0;border:0;min-width:0}
.proof-integration .integration-restriction{padding:12px;margin:0}
.proof-integration[data-proof-disabled="true"] .proof-canvas{visibility:hidden}
`
  );
}
